/**
 * Controls screen: keyboard diagram with the three layouts (WASD + mouse, WASD + J K L,
 * Arrows + Z X C V), plus gamepad and touch. Bindings are read from core KEY_MAP.
 */
import { KEY_MAP, type Action } from '@core/input';
import type { ActionLabelKey } from '@core/tree';
import { el, escapeHtml } from '../dom';
import { navButton } from '../nav';
import { keyLabel } from '../keys';

type Act = Action | 'move';

const ROWS: Array<Array<[string, number?]>> = [
  [['Escape', 1.2], ['KeyQ'], ['KeyW'], ['KeyE'], ['KeyR'], ['KeyT'], ['KeyY'], ['KeyU'], ['KeyI'], ['KeyO'], ['KeyP']],
  [['CapsLock', 1.6], ['KeyA'], ['KeyS'], ['KeyD'], ['KeyF'], ['KeyG'], ['KeyH'], ['KeyJ'], ['KeyK'], ['KeyL'], ['Enter', 1.6]],
  [['ShiftLeft', 2], ['KeyZ'], ['KeyX'], ['KeyC'], ['KeyV'], ['KeyB'], ['KeyN'], ['KeyM'], ['Comma'], ['Period'], ['Slash'], ['ShiftRight', 2]],
  [['ControlLeft', 1.4], ['Space', 6.4], ['ControlRight', 1.4]],
];
const ARROWS: Array<Array<string | null>> = [
  [null, 'ArrowUp', null],
  ['ArrowLeft', 'ArrowDown', 'ArrowRight'],
];
const MOVE = new Set(['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

const LAYOUTS: Array<{ name: string; sub: string; keys: string[]; mouse?: boolean }> = [
  { name: 'WASD + Mouse', sub: 'Mouse to look · click to attack', keys: ['KeyW', 'KeyA', 'KeyS', 'KeyD', 'Space', 'ShiftLeft', 'KeyF', 'KeyQ', 'KeyE', 'Escape'], mouse: true },
  { name: 'WASD + J K L', sub: 'Left hand moves, right hand fights', keys: ['KeyW', 'KeyA', 'KeyS', 'KeyD', 'Space', 'KeyJ', 'KeyK', 'KeyL', 'KeyI', 'Escape'] },
  { name: 'Arrows + Z X C V', sub: 'Right hand moves, left hand fights', keys: ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'KeyZ', 'KeyX', 'KeyC', 'KeyV', 'Space', 'Slash', 'ControlRight', 'ShiftRight', 'Period', 'Enter', 'Escape'] },
];

const DEFAULT_NAMES: Record<Act, string> = { move: 'Move', attack: 'Attack', dash: 'Dash', jump: 'Jump', ability: 'Ability', interact: 'Use / Evolve', pause: 'Pause' };

function actOf(code: string): Act | undefined {
  return MOVE.has(code) ? 'move' : KEY_MAP[code];
}

export function buildControls(opts: { labels?: Partial<Record<ActionLabelKey, string>>; onBack: () => void; isTouch: boolean }): HTMLElement {
  const names: Record<Act, string> = { ...DEFAULT_NAMES };
  for (const [k, v] of Object.entries(opts.labels ?? {})) if (v) names[k as Act] = k === 'interact' && /^evolve$/i.test(v) ? 'Use / Evolve' : v;

  const panel = el('div', 'panel glass controls-panel');
  const head = el('div', 'panel-head', '<h2>Controls</h2>');
  head.append(navButton('Back', 'small ghost', opts.onBack));
  const tabs = el('div', 'tabs');
  const board = el('div', 'kb-wrap');
  const legend = el('div', 'kb-legend');
  legend.innerHTML = (['move', 'attack', 'dash', 'jump', 'ability', 'interact', 'pause'] as Act[])
    .map((a) => `<span class="a-${a}"><i></i>${escapeHtml(names[a])}</span>`)
    .join('');

  const key = (code: string, w: number, on: Set<string>) => {
    const act = on.has(code) ? actOf(code) : undefined;
    return `<div class="kk${act ? ` on a-${act}` : ''}" style="flex:${w}"><b>${escapeHtml(keyLabel(code).replace('Control', 'Ctrl').replace('CapsLock', 'Caps').replace('Comma', ','))}</b>${act ? `<small>${escapeHtml(names[act])}</small>` : ''}</div>`;
  };
  const render = (i: number) => {
    const L = LAYOUTS[i];
    const on = new Set(L.keys);
    tabs.querySelectorAll('.tab').forEach((t, j) => t.classList.toggle('active', i === j));
    const main = ROWS.map((r) => `<div class="kr">${r.map(([c, w]) => key(c, w ?? 1, on)).join('')}</div>`).join('');
    const arrows = ARROWS.map((r) => `<div class="kr">${r.map((c) => (c ? key(c, 1, on) : '<div class="kk gap"></div>')).join('')}</div>`).join('');
    board.innerHTML = `<div class="kb-sub">${escapeHtml(L.sub)}</div><div class="kb"><div class="kb-main">${main}</div><div class="kb-arrows">${arrows}</div>${
      L.mouse ? `<div class="kb-mouse"><div class="mouse"><span class="l on a-attack"></span><span class="r"></span></div><small>Look · click = ${escapeHtml(names.attack)}</small></div>` : ''
    }</div>`;
  };
  LAYOUTS.forEach((L, i) => {
    const b = el('button', 'tab', escapeHtml(L.name));
    b.dataset.nav = '';
    if (i === 0) b.dataset.navDefault = '';
    b.addEventListener('click', () => render(i));
    b.addEventListener('navfocus', () => render(i));
    tabs.append(b);
  });

  const other = el(
    'div',
    'ctl-other',
    `<div><h3>Gamepad</h3><ul>
      <li><kbd class="pad">L</kbd> ${escapeHtml(names.move)} · <kbd class="pad">R</kbd> Look</li>
      <li><kbd class="pad">X</kbd> / RT ${escapeHtml(names.attack)}</li>
      <li><kbd class="pad">B</kbd> / LT ${escapeHtml(names.dash)}</li>
      <li><kbd class="pad">A</kbd> ${escapeHtml(names.jump)} · <kbd class="pad">Y</kbd> ${escapeHtml(names.ability)}</li>
      <li>Back (hold) ${escapeHtml(names.interact)} · Start ${escapeHtml(names.pause)}</li></ul></div>
     <div><h3>Touch</h3><ul>
      <li>Left thumb: floating joystick</li>
      <li>Right side: drag to look</li>
      <li>Big button: ${escapeHtml(names.attack)} · small buttons: ${escapeHtml(names.dash)}, ${escapeHtml(names.jump)}, ${escapeHtml(names.ability)}</li>
      <li>Hold the glowing prompt to evolve · pause button top-right</li></ul></div>`,
  );
  panel.append(head, tabs, board, legend, other);
  render(opts.isTouch ? 0 : 0);
  return panel;
}
